import React, { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import GroupChangeSummary from './GroupChangeSummary';
import { Modal, CircularProgress, Checkbox, Menu, MenuItem } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import {
  getMemberGroupMembers,
  addMemberGroupMembers,
  removeMemberGroupMembers,
} from '../../../../api/member-group';
import { errorMessages } from '../../../../utils/methods';
// 좌우 2패널 껍데기는 문자 수신자 편집·회원 편집과 같은 디자인을 공유한다.
import {
  EditModalContainer,
  EditHeader,
  EditHeaderTitle,
  EditHeaderClose,
  EditBody,
  ListHead,
  ListScroll,
  PoolRow,
  RowCheck,
  Cell,
  HeadCell,
  NameCell,
  EmptyMessage,
  PickedHeader,
  PickedTitle,
  EditFooter,
  FooterActions,
  CancelButton,
  ApplyButton,
} from '../MemberRecipientPickerModalStyled';
import {
  TransferArrowColumn,
  TransferArrowButton,
  TransferCopyLabel,
  TransferResetButton,
  TransferPanel,
  TransferGroupPicker,
  TransferGroupName,
  TransferGroupChevron,
  MenuGroupName,
  MenuGroupCount,
} from './MemberGroupModalStyled';

const phoneOf = member => member.phoneNumber || '-';

const LEFT = 'LEFT';
const RIGHT = 'RIGHT';

/**
 * 그룹 간 회원 이동 모달.
 * 좌·우 모두 드롭다운으로 그룹을 고르고, 화살표로 **양방향** 이동한다.
 * 옮긴 결과는 양쪽 목록에 즉시 미리보기로 반영되고, '적용'을 눌러야 서버에 나간다.
 * 복사(원래 그룹에도 남기기)를 켜면 미리보기에서도 원래 그룹에 그대로 남는다.
 */
const MemberGroupTransferModal = ({ open, onClose, groups, sourceGroup, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);

  const [leftGroupId, setLeftGroupId] = useState(null);
  const [rightGroupId, setRightGroupId] = useState(null);
  const [copyMode, setCopyMode] = useState(false);
  const [leftChecked, setLeftChecked] = useState(() => new Set());
  const [rightChecked, setRightChecked] = useState(() => new Set());
  // 이번 편집에서 옮겨 담은 회원. 방향별로 따로 들고 있다가 적용할 때 한 번에 보낸다.
  const [movingRight, setMovingRight] = useState(() => new Map()); // 왼쪽 → 오른쪽
  const [movingLeft, setMovingLeft] = useState(() => new Map()); // 오른쪽 → 왼쪽
  // 이미 상대 그룹에 있어 옮기지 못한 회원 — 잠깐 강조해서 어디가 겹쳤는지 보여준다.
  const [duplicateIds, setDuplicateIds] = useState(() => new Set());
  // 화살표를 누르기 직전 상태를 쌓아 둔다. '되돌리기'가 한 단계씩 무른다.
  const [history, setHistory] = useState([]);
  const [groupMenu, setGroupMenu] = useState({ anchor: null, side: null });
  const [saving, setSaving] = useState(false);

  // 드래그로 여러 줄을 한 번에 고른다. 시작 행의 목표 상태를 지나가는 행에 그대로 적용한다.
  const dragModeRef = useRef(null);

  const resetStaging = () => {
    setMovingRight(new Map());
    setMovingLeft(new Map());
    setLeftChecked(new Set());
    setRightChecked(new Set());
    setDuplicateIds(new Set());
    setHistory([]);
  };

  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setMovingRight(new Map(last.right));
    setMovingLeft(new Map(last.left));
    setHistory(prev => prev.slice(0, -1));
    setLeftChecked(new Set());
    setRightChecked(new Set());
  };

  useEffect(() => {
    if (!open) return;
    const first = sourceGroup?.id ?? groups[0]?.id ?? null;
    setLeftGroupId(first);
    setRightGroupId(groups.find(group => group.id !== first)?.id ?? null);
    setCopyMode(false);
    resetStaging();
    // 열 때 한 번만 초기화한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const stopDrag = () => {
      dragModeRef.current = null;
    };
    window.addEventListener('mouseup', stopDrag);
    return () => window.removeEventListener('mouseup', stopDrag);
  }, []);

  const { data: leftMembers = [], isLoading: leftLoading } = useQuery({
    queryKey: ['memberGroupMembers', leftGroupId],
    queryFn: () => getMemberGroupMembers(user.token, leftGroupId),
    enabled: open && !!leftGroupId,
  });

  const { data: rightMembers = [], isLoading: rightLoading } = useQuery({
    queryKey: ['memberGroupMembers', rightGroupId],
    queryFn: () => getMemberGroupMembers(user.token, rightGroupId),
    enabled: open && !!rightGroupId,
  });

  const leftIds = useMemo(() => new Set(leftMembers.map(m => m.memberId)), [leftMembers]);
  const rightIds = useMemo(() => new Set(rightMembers.map(m => m.memberId)), [rightMembers]);

  // 화면에 보이는 목록 = 기존 소속(복사로 담은 회원은 그대로 남는다) + 들어온 회원
  // 담을 때의 복사 여부를 항목마다 들고 있어서, 체크를 나중에 켜고 꺼도 이미 담은 건은 안 바뀐다.
  const leftRows = useMemo(
    () => [
      ...leftMembers.filter(member => {
        const staged = movingRight.get(member.memberId);
        return !staged || staged.copy;
      }),
      ...[...movingLeft.values()]
        .filter(entry => !leftIds.has(entry.member.memberId))
        .map(entry => entry.member),
    ],
    [leftMembers, movingRight, movingLeft, leftIds],
  );

  const rightRows = useMemo(
    () => [
      ...rightMembers.filter(member => {
        const staged = movingLeft.get(member.memberId);
        return !staged || staged.copy;
      }),
      ...[...movingRight.values()]
        .filter(entry => !rightIds.has(entry.member.memberId))
        .map(entry => entry.member),
    ],
    [rightMembers, movingLeft, movingRight, rightIds],
  );

  const setRowChecked = (setter, memberId, on) =>
    setter(prev => {
      if (prev.has(memberId) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(memberId);
      else next.delete(memberId);
      return next;
    });

  const handleRowMouseDown = (setter, checked, memberId) => {
    const willCheck = !checked.has(memberId);
    dragModeRef.current = willCheck;
    setRowChecked(setter, memberId, willCheck);
  };

  const handleRowMouseEnter = (setter, memberId) => {
    if (dragModeRef.current === null) return;
    setRowChecked(setter, memberId, dragModeRef.current);
  };

  const flashDuplicates = (duplicated, targetName, side) => {
    const names = duplicated.map(member => member.name || '-').join(', ');
    toast.warning(`${names}${t(' 님은 이미')} ${targetName}${t('에 있습니다')}`);
    setDuplicateIds(new Set(duplicated.map(member => member.memberId)));
    // 목록이 길면 겹친 행이 화면 밖에 있다. 첫 번째 행으로 데려간다.
    setTimeout(() => {
      document
        .querySelector(`[data-transfer-row="${side}-${duplicated[0].memberId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    setTimeout(() => setDuplicateIds(new Set()), 2600);
  };

  const leftGroup = groups.find(group => group.id === leftGroupId) || null;
  const rightGroup = groups.find(group => group.id === rightGroupId) || null;

  /**
   * 한쪽에서 체크한 회원을 반대쪽으로 옮겨 담는다.
   * 반대쪽에 이미 소속된 회원은 옮기지 않고 그 행을 강조한다.
   * 반대 방향으로 담아 뒀던 회원을 되돌리는 경우는 그 예약만 취소한다.
   */
  const move = direction => {
    const toRight = direction === RIGHT;
    const rows = toRight ? leftRows : rightRows;
    const checked = toRight ? leftChecked : rightChecked;
    const targetIds = toRight ? rightIds : leftIds;
    const pendingOther = toRight ? movingLeft : movingRight;
    const setPendingOther = toRight ? setMovingLeft : setMovingRight;
    const setPendingThis = toRight ? setMovingRight : setMovingLeft;
    const targetName = (toRight ? rightGroup?.name : leftGroup?.name) || t('상대 그룹');

    const picked = rows.filter(member => checked.has(member.memberId));
    if (!picked.length) return;

    // 반대 방향으로 담아 둔 회원이면 그 예약을 무르는 것이다.
    const undo = picked.filter(member => pendingOther.has(member.memberId));
    const rest = picked.filter(member => !pendingOther.has(member.memberId));
    const duplicated = rest.filter(member => targetIds.has(member.memberId));
    const fresh = rest.filter(member => !targetIds.has(member.memberId));

    // 고른 게 전부 중복이면 담긴 것이 하나도 없다. 그때까지 히스토리를 쌓으면
    // 되돌릴 게 없는데 '되돌리기'가 켜져 사용자가 무언가 옮겼다고 오해한다.
    if (!undo.length && !fresh.length) {
      if (duplicated.length) {
        flashDuplicates(duplicated, targetName, toRight ? RIGHT : LEFT);
        (toRight ? setLeftChecked : setRightChecked)(new Set());
      }
      return;
    }

    // 무를 수 있게 지금 상태를 쌓아 둔다(최근 30단계).
    setHistory(prev => [...prev, { right: new Map(movingRight), left: new Map(movingLeft) }].slice(-30));

    if (undo.length) {
      setPendingOther(prev => {
        const next = new Map(prev);
        undo.forEach(member => next.delete(member.memberId));
        return next;
      });
    }
    if (fresh.length) {
      setPendingThis(prev => {
        const next = new Map(prev);
        // 지금 체크 상태를 항목에 굳혀 둔다. 나중에 체크를 바꿔도 이 건은 그대로다.
        fresh.forEach(member => next.set(member.memberId, { member, copy: copyMode }));
        return next;
      });
    }
    if (duplicated.length) {
      flashDuplicates(duplicated, targetName, toRight ? RIGHT : LEFT);
    }

    // 중복이든 아니든 체크는 모두 푼다.
    if (toRight) setLeftChecked(new Set());
    else setRightChecked(new Set());
  };

  const changeGroup = (setter, value) => {
    setter(value);
    resetStaging();
  };

  const toRightIds = [...movingRight.keys()];
  const toLeftIds = [...movingLeft.keys()];
  const totalMoves = toRightIds.length + toLeftIds.length;

  // 담을 때의 복사 여부로 나눈다. 복사분은 추가만, 이동분은 추가 후 기존 그룹에서 제거.
  const splitByMode = pending => ({
    copies: [...pending.values()].filter(entry => entry.copy).map(entry => entry.member.memberId),
    moves: [...pending.values()].filter(entry => !entry.copy).map(entry => entry.member.memberId),
  });

  /*
   * 이동은 두 그룹을 동시에 건드리는데, 누르기 전에는 어느 쪽이 몇 명이 되는지 안 보인다 (#78).
   * 복사(copy)는 원본이 남고 이동(move)만 빠지므로 그 차이도 전후 숫자로 드러난다.
   */
  const [confirmOpen, setConfirmOpen] = useState(false);

  const changeRows = useMemo(() => {
    if (!leftGroupId || !rightGroupId) return [];
    const right = splitByMode(movingRight);
    const left = splitByMode(movingLeft);
    const leftGroup = groups.find(group => group.id === leftGroupId);
    const rightGroup = groups.find(group => group.id === rightGroupId);
    const leftBefore = leftMembers.length;
    const rightBefore = rightMembers.length;
    // 들어오는 건 이동·복사 모두, 나가는 건 이동만이다.
    const leftAfter = leftBefore + toLeftIds.length - right.moves.length;
    const rightAfter = rightBefore + toRightIds.length - left.moves.length;
    const toneOf = (before, after) => (after > before ? 'up' : after < before ? 'down' : undefined);
    return [
      {
        label: leftGroup?.name || '왼쪽 그룹',
        before: `${leftBefore}명`,
        after: `${leftAfter}명`,
        tone: toneOf(leftBefore, leftAfter),
      },
      {
        label: rightGroup?.name || '오른쪽 그룹',
        before: `${rightBefore}명`,
        after: `${rightAfter}명`,
        tone: toneOf(rightBefore, rightAfter),
      },
    ];
  }, [leftGroupId, rightGroupId, groups, leftMembers, rightMembers,
    movingLeft, movingRight, toLeftIds, toRightIds]);

  const handleApply = async () => {
    if (saving || !totalMoves || !leftGroupId || !rightGroupId) return;
    setSaving(true);
    try {
      // 방향마다 대상에 먼저 넣고, 이동분만 기존 그룹에서 뺀다.
      const right = splitByMode(movingRight);
      const left = splitByMode(movingLeft);
      if (toRightIds.length) {
        await addMemberGroupMembers(user.token, rightGroupId, toRightIds);
        if (right.moves.length) {
          await removeMemberGroupMembers(user.token, leftGroupId, right.moves);
        }
      }
      if (toLeftIds.length) {
        await addMemberGroupMembers(user.token, leftGroupId, toLeftIds);
        if (left.moves.length) {
          await removeMemberGroupMembers(user.token, rightGroupId, left.moves);
        }
      }
      const movedCount = right.moves.length + left.moves.length;
      const copiedCount = right.copies.length + left.copies.length;
      const parts = [];
      if (movedCount) parts.push(`${movedCount}${t('명 이동')}`);
      if (copiedCount) parts.push(`${copiedCount}${t('명 복사')}`);
      toast.success(parts.join(', '));
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setSaving(false);
    }
  };

  // 셀렉트의 기본 트리거는 긴 이름에서 글자가 잘려 깨져 보였다.
  // 이름은 가운데에 온전히 두고, 옆의 갈매기 버튼으로 메뉴를 연다.
  const openGroupMenu = (event, side) => setGroupMenu({ anchor: event.currentTarget, side });

  const renderGroupPicker = (side, groupId, onChangeGroup) => {
    const current = groups.find(group => group.id === groupId);
    return (
      <TransferGroupPicker
        isOpen={groupMenu.side === side && !!groupMenu.anchor}
        onClick={event => openGroupMenu(event, side)}>
        <TransferGroupName title={current?.name || ''}>
          {current?.name || t('그룹 선택')}
        </TransferGroupName>
        <TransferGroupChevron isOpen={groupMenu.side === side && !!groupMenu.anchor}>
          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
        </TransferGroupChevron>
      </TransferGroupPicker>
    );
  };

  const renderPanel = (side, config) => {
    const { groupId, onChangeGroup, rows, loading, incoming, outgoing, originIds, checked, setChecked } = config;
    return (
      <TransferPanel isStaged={incoming.size > 0}>
        <PickedHeader>
          {renderGroupPicker(side, groupId, onChangeGroup)}
          <PickedTitle sx={{ flexShrink: 0 }}>{`${rows.length}${t('명')}`}</PickedTitle>
        </PickedHeader>
        <ListHead>
          <HeadCell w="30px" />
          <HeadCell w="96px">{t('회원명')}</HeadCell>
          <HeadCell grow>{t('전화번호')}</HeadCell>
          <HeadCell w="52px">{t('상태')}</HeadCell>
        </ListHead>
        <ListScroll>
          {loading ? (
            <EmptyMessage>
              <CircularProgress size={20} sx={{ color: '#4ab3bc' }} />
            </EmptyMessage>
          ) : rows.length === 0 ? (
            <EmptyMessage>{t('회원이 없습니다')}</EmptyMessage>
          ) : (
            rows.map((member, index) => {
              const incomingEntry = incoming.get(member.memberId);
              const isIncoming = !!incomingEntry && !originIds.has(member.memberId);
              const copiedOut = outgoing.get(member.memberId)?.copy && !isIncoming;
              const isDuplicate = duplicateIds.has(member.memberId);
              return (
                <PoolRow
                  key={member.memberId}
                  data-transfer-row={`${side}-${member.memberId}`}
                  odd={index % 2 === 1}
                  onMouseDown={() => handleRowMouseDown(setChecked, checked, member.memberId)}
                  onMouseEnter={() => handleRowMouseEnter(setChecked, member.memberId)}
                  style={{
                    userSelect: 'none',
                    backgroundColor: isDuplicate
                      ? '#FDECEA'
                      : isIncoming
                        ? '#F0FAFB'
                        : copiedOut
                          ? '#FFF7E8'
                          : undefined,
                    boxShadow: isDuplicate
                      ? 'inset 0 0 0 2px #E57373'
                      : copiedOut
                        ? 'inset 0 0 0 1px #F0C486'
                        : undefined,
                    transition: 'background-color 200ms ease, box-shadow 200ms ease',
                  }}>
                  <RowCheck checked={checked.has(member.memberId)}>
                    {checked.has(member.memberId) ? '✓' : ''}
                  </RowCheck>
                  <NameCell w="96px" center>{member.name || '-'}</NameCell>
                  <Cell grow center>{phoneOf(member)}</Cell>
                  <Cell
                    w="52px"
                    center
                    sx={{
                      color: isDuplicate
                        ? '#C2403D'
                        : isIncoming
                          ? '#0F7F85'
                          : copiedOut
                            ? '#B26A00'
                            : '#B9C0C4',
                      fontWeight: isDuplicate || copiedOut ? 700 : 400,
                    }}>
                    {isDuplicate
                      ? t('중복')
                      : isIncoming
                        ? incomingEntry.copy
                          ? t('복사')
                          : t('추가')
                        : copiedOut
                          ? t('복사됨')
                          : t('소속')}
                  </Cell>
                </PoolRow>
              );
            })
          )}
        </ListScroll>
      </TransferPanel>
    );
  };

  return (
    <>
    <Modal
      open={open}
      // 옮겨 둔 것이 있으면 배경 클릭·ESC 로 닫지 않는다. 적용 전이라 되돌릴 수단이 없다.
      onClose={(event, reason) => {
        if (saving) return;
        if (totalMoves && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          toast.info(t('옮겨 둔 회원이 있습니다. 적용하거나 취소를 눌러 주세요'));
          return;
        }
        onClose();
      }}>
      <EditModalContainer sx={{ height: '620px' }}>
        <EditHeader>
          <EditHeaderTitle>{t('그룹 간 회원 이동')}</EditHeaderTitle>
          <EditHeaderClose onClick={onClose}>✕</EditHeaderClose>
        </EditHeader>

        <EditBody sx={{ alignItems: 'stretch' }}>
          {renderPanel(LEFT, {
            groupId: leftGroupId,
            onChangeGroup: value => changeGroup(setLeftGroupId, value),
            rows: leftRows,
            loading: leftLoading,
            incoming: movingLeft,
            outgoing: movingRight,
            originIds: leftIds,
            checked: leftChecked,
            setChecked: setLeftChecked,
          })}

          {/* 가운데: 양방향 화살표 */}
          <TransferArrowColumn>
            <TransferArrowButton
              disabled={leftChecked.size === 0}
              onClick={() => move(RIGHT)}
              title={t('오른쪽 그룹으로')}>
              <ArrowForwardIcon sx={{ fontSize: 20 }} />
            </TransferArrowButton>
            <TransferArrowButton
              disabled={rightChecked.size === 0}
              onClick={() => move(LEFT)}
              title={t('왼쪽 그룹으로')}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </TransferArrowButton>
          </TransferArrowColumn>

          {renderPanel(RIGHT, {
            groupId: rightGroupId,
            onChangeGroup: value => changeGroup(setRightGroupId, value),
            rows: rightRows,
            loading: rightLoading,
            incoming: movingRight,
            outgoing: movingLeft,
            originIds: rightIds,
            checked: rightChecked,
            setChecked: setRightChecked,
          })}
        </EditBody>

        <EditFooter>
          <TransferCopyLabel>
            <Checkbox
              size="small"
              checked={copyMode}
              onChange={e => setCopyMode(e.target.checked)}
              sx={{ padding: 0, marginRight: '6px', color: '#9AA3A9', '&.Mui-checked': { color: '#4AB3BC' } }}
            />
            {t('기존 그룹에도 남기기(복사)')}
          </TransferCopyLabel>
          <FooterActions>
            <TransferResetButton
              sx={history.length ? null : { opacity: 0.45, pointerEvents: 'none' }}
              onClick={undo}>
              {history.length > 1 ? `${t('되돌리기')} (${history.length})` : t('되돌리기')}
            </TransferResetButton>
            <TransferResetButton
              sx={totalMoves ? null : { opacity: 0.45, pointerEvents: 'none' }}
              onClick={resetStaging}>
              {t('초기화')}
            </TransferResetButton>
            <CancelButton onClick={onClose}>{t('취소')}</CancelButton>
            <ApplyButton
              onClick={() => !saving && totalMoves > 0 && setConfirmOpen(true)}
              sx={saving || !totalMoves ? { opacity: 0.5, pointerEvents: 'none' } : null}>
              {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('적용')}
            </ApplyButton>
          </FooterActions>
        </EditFooter>

        {/* 갈매기 버튼으로 여는 그룹 선택 메뉴. 반대편이 고른 그룹은 목록에서 뺀다. */}
        <Menu
          open={!!groupMenu.anchor}
          anchorEl={groupMenu.anchor}
          onClose={() => setGroupMenu({ anchor: null, side: null })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            paper: {
              sx: {
                marginTop: '6px',
                minWidth: '240px',
                maxWidth: '320px',
                maxHeight: '320px',
                borderRadius: '12px',
                padding: '6px',
                border: '1px solid #E4E8EB',
                boxShadow: '0 10px 28px rgba(0, 0, 0, 0.14)',
              },
            },
            list: { sx: { padding: 0 } },
          }}>
          {groups
            .filter(group => group.id !== (groupMenu.side === LEFT ? rightGroupId : leftGroupId))
            .map(group => {
              const isCurrent = group.id === (groupMenu.side === LEFT ? leftGroupId : rightGroupId);
              return (
                <MenuItem
                  key={group.id}
                  selected={isCurrent}
                  onClick={() => {
                    changeGroup(groupMenu.side === LEFT ? setLeftGroupId : setRightGroupId, group.id);
                    setGroupMenu({ anchor: null, side: null });
                  }}
                  sx={{
                    gap: '8px',
                    borderRadius: '8px',
                    padding: '9px 10px',
                    fontSize: '13px',
                    '&.Mui-selected': { backgroundColor: '#E4F5F6' },
                    '&.Mui-selected:hover': { backgroundColor: '#D8F0F2' },
                  }}>
                  <CheckIcon
                    sx={{ fontSize: 16, color: isCurrent ? '#0F7F85' : 'transparent', flexShrink: 0 }}
                  />
                  <MenuGroupName isCurrent={isCurrent}>{group.name}</MenuGroupName>
                  <MenuGroupCount>{`${group.memberCount}${t('명')}`}</MenuGroupCount>
                </MenuItem>
              );
            })}
        </Menu>
      </EditModalContainer>
    </Modal>

    <ConfirmModal
      open={confirmOpen}
      onClose={() => !saving && setConfirmOpen(false)}
      onConfirm={() => {
        if (saving) return;
        setConfirmOpen(false);
        handleApply();
      }}
      message={'두 그룹의 인원이 이렇게 바뀝니다.'}
      highlightText="두 그룹"
      highlightColor="#0F7F85"
      cancelText="취소"
      confirmText="적용"
      confirmColor="#0F7F85"
      detail={<GroupChangeSummary rows={changeRows} />}
    />
    </>
  );
};

export default MemberGroupTransferModal;
